/* hooks/drawerStack.js — чистая модель drawer-стека «Продукции» (?edit=type-id,…).
   Без React и без window: хук useDrawerStack (hooks/index.jsx) даёт ей живой адрес и сторож.

   Инварианты (kitchen-ux A2):
   · в стеке только известные типы — card | ttk | menu | pos (K-30: мусорный ?edit=foo-1
     больше не рисует заглушку);
   · корень стека не теряется (C-07: меню → блюдо → п/ф → сырьё — меню остаётся первым);
     потолок глубины (длина адреса) режет СЕРЕДИНУ; крошки сворачивают её в «…»;
   · уже открытая сущность не открывается второй раз — push возвращает к её уровню;
   · ?etab и ?v принадлежат ВЕРХНЕМУ редактору (K-53): смена верха их снимает;
   · все переходы — через один guard (K-01): пока он не разрешил, адрес не трогается;
     запись считается от ЖИВОГО стека в момент записи (сохранение в диалоге сторожа
     могло заменить new на настоящий id). */

export const STACK_TYPES = ['card', 'ttk', 'menu', 'pos'];
export const MAX_STACK_DEPTH = 8;
export const TOP_SCOPED_PARAMS = ['etab', 'v'];

export function parseStack(raw) {
  if (!raw) return [];
  return String(raw).split(',').map((tok) => {
    const t = tok.trim();
    const i = t.indexOf('-');
    if (i <= 0) return null;
    const type = t.slice(0, i);
    const id = t.slice(i + 1);
    if (!STACK_TYPES.includes(type) || !id) return null;
    return { type, id };
  }).filter(Boolean);
}

export function serializeStack(stack) {
  return (stack || []).map((e) => `${e.type}-${e.id}`).join(',');
}

export const entryKey = (e) => (e ? `${e.type}-${e.id}` : null);
export const topKey = (stack) => (stack && stack.length ? entryKey(stack[stack.length - 1]) : null);
const sameEntry = (a, b) => !!a && !!b && a.type === b.type && String(a.id) === String(b.id);
const norm = (entry) => ({ type: entry.type, id: String(entry.id) });

export function pushEntry(stack, entry, max = MAX_STACK_DEPTH) {
  const idx = stack.findIndex((e) => sameEntry(e, entry));
  if (idx >= 0) return stack.slice(0, idx + 1);
  const next = [...stack, norm(entry)];
  if (next.length <= max) return next;
  return [next[0], ...next.slice(next.length - (max - 1))];
}

export const popEntry = (stack) => stack.slice(0, -1);
export const popToEntry = (stack, index) => (index < 0 ? [] : stack.slice(0, index + 1));
export const replaceTopEntry = (stack, entry) => [...stack.slice(0, -1), norm(entry)];

// URL после смены стека: верх сменился — его ?etab/?v уходят вместе с ним. keepTopScoped —
// та же сущность под новым id (replaceTop: создали new → настоящий id) — вкладка остаётся.
export function stackParams(prev, nextStack, { keepTopScoped = false } = {}) {
  const sp = new URLSearchParams(prev);
  const prevTop = topKey(parseStack(sp.get('edit')));
  if (nextStack.length) sp.set('edit', serializeStack(nextStack)); else sp.delete('edit');
  if (!keepTopScoped && topKey(nextStack) !== prevTop) TOP_SCOPED_PARAMS.forEach((k) => sp.delete(k));
  return sp;
}

// Смонтирован ли редактор записи `key` по адресу loc: он — верх ?edit на том же пути.
export function isEntryAliveAt(key, loc, pathname) {
  if (!loc) return false;
  if (pathname != null && loc.pathname !== pathname) return false;
  return topKey(parseStack(new URLSearchParams(loc.search || '').get('edit'))) === key;
}

/* Навигатор. readStack() — стек в адресе сейчас; writeStack(compute) — запись, compute
   получает живой стек; guard(commit, {keys}) — сторож, вернёт/разрешит true|false.
   opts.before — выполнить внутри разрешённого перехода прямо перед записью (подсказка
   для следующего редактора в sessionStorage пишется, только если переход состоялся). */
export function createStackNavigator({ readStack, writeStack, guard }) {
  const run = (compute, opts) => {
    const cur = readStack();
    const curTop = topKey(cur);
    const commit = () => {
      if (opts && typeof opts.before === 'function') opts.before();
      writeStack(compute, opts);
      return true;
    };
    if (!guard || !curTop || topKey(compute(cur)) === curTop) return Promise.resolve(commit());
    return Promise.resolve(guard(commit, { keys: [curTop] })).then((ok) => !!ok);
  };
  return {
    push: (type, id, opts) => run((s) => pushEntry(s, { type, id }), opts),
    pop: (opts) => run(popEntry, opts),
    popTo: (index, opts) => run((s) => popToEntry(s, index), opts),
    replaceTop: (type, id, opts) => run((s) => replaceTopEntry(s, { type, id }), { replace: true, keepTopScoped: true, ...(opts || {}) }),
    clear: (opts) => run(() => [], opts),
  };
}
